<template>
  <div class="ledger-page">
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h3 class="ml-1 mb-2">
            {{ $t('ledger_coa_title') }}
          </h3>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="mb-1"
        >
          <b-form-input
            v-model="filter.keyword"
            :placeholder="$t('ledger_search')"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.type"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="typeOptions"
            :reduce="val => val.value"
            :placeholder="$t('ledger_type')"
            :label="localeLabel"
            clearable
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
          class="mb-1"
        >
          <v-select
            v-model="filter.is_active"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="activeOptions"
            :reduce="val => val.value"
            :placeholder="$t('ledger_active')"
            :label="localeLabel"
            clearable
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.accepts_posting"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="postingOptions"
            :reduce="val => val.value"
            :placeholder="$t('ledger_accepts_posting')"
            :label="localeLabel"
            clearable
          />
        </b-col>
      </b-row>
    </b-card>

    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <div class="d-flex align-items-center">
          <span class="text-muted small mr-1">{{ flatRows.length }}</span>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-50"
            @click="collapseAll"
          >
            {{ $t('ledger_collapse_all') }}
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="expandAll"
          >
            {{ $t('ledger_expand_all') }}
          </b-button>
        </div>
        <b-button
          v-if="hasPermission('create chart_of_accounts')"
          variant="primary"
          class="rounded-pill"
          @click="openCreate"
        >
          {{ $t('ledger_new_account') }}
        </b-button>
      </div>
      <b-form-input
        v-if="partyParentOpen"
        v-model="partyQuery"
        size="sm"
        class="mb-1"
        :placeholder="$t('ledger_party_filter')"
      />

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!flatRows.length"
        class="text-center py-3 text-muted"
      >
        {{ $t('ledger_empty_accounts') }}
      </div>

      <div
        v-else
        class="d-none d-md-block"
      >
        <b-table
          :items="flatRows"
          :fields="fields"
          primary-key="rowKey"
          responsive
          hover
          small
          class="ledger-dense-table"
          thead-class="ledger-sticky-head"
        >
          <template #cell(code)="data">
            <span
              class="ledger-indent"
              :style="{ paddingInlineStart: `${data.item.depth * 18}px` }"
            >
              <button
                v-if="data.item.hasChildren"
                type="button"
                class="ledger-tree-toggle"
                @click.stop="toggleExpand(data.item)"
              >
                <feather-icon
                  :icon="isExpanded(data.item) ? 'ChevronDownIcon' : (isRtl ? 'ChevronLeftIcon' : 'ChevronRightIcon')"
                  size="16"
                />
              </button>
              <span
                v-else
                class="ledger-tree-spacer"
              />
              <feather-icon
                v-if="data.item.isTill"
                icon="CreditCardIcon"
                size="14"
                class="mr-50"
              />
              <feather-icon
                v-else-if="!data.item.isPartyHint && !data.item.accepts_posting"
                icon="FolderIcon"
                size="14"
                class="mr-50"
              />
              <strong>{{ data.item.code }}</strong>
              <b-badge
                v-if="data.item.isPartyControl"
                variant="light"
                class="ml-50"
              >
                {{ formatCount(data.item.childCount) }}
              </b-badge>
            </span>
          </template>
          <template #cell(name)="data">
            <span
              v-if="data.item.isPartyHint"
              class="text-muted small"
            >
              {{ data.item.isPartyEmpty ? $t('ledger_party_filter_empty') : $t('ledger_party_filter_more', { shown: data.item.shown, total: data.item.total }) }}
            </span>
            <span
              v-else
              :class="{ 'ledger-inactive': !data.item.is_active }"
            >
              {{ accountName(data.item) }}
            </span>
          </template>
          <template #cell(type)="data">
            <template v-if="!data.item.isPartyHint">
              <b-badge
                v-if="data.item.isTill"
                variant="warning"
              >
                {{ tillTypeLabel(data.item.type) }}
              </b-badge>
              <b-badge
                v-else
                :variant="typeBadgeVariant(enumValue(data.item.type))"
              >
                {{ enumLabel(data.item.type, $i18n.locale) }}
              </b-badge>
            </template>
          </template>
          <template #cell(nature)="data">
            <template v-if="!data.item.isPartyHint">
              <span
                v-if="data.item.isTill"
                class="ledger-num"
              >
                {{ money(data.item.current_balance) }}
              </span>
              <span v-else>
                {{ enumLabel(data.item.nature, $i18n.locale) }}
              </span>
            </template>
          </template>
          <template #cell(flags)="data">
            <template v-if="!data.item.isPartyHint">
              <b-badge
                v-if="partyBadgeKey(data.item)"
                variant="light-primary"
                class="mr-50"
              >
                {{ $t(partyBadgeKey(data.item)) }}
              </b-badge>
              <b-badge
                v-if="data.item.isTill"
                variant="warning"
              >
                {{ $t('ledger_till') }}
              </b-badge>
              <b-badge
                v-else-if="!data.item.accepts_posting"
                variant="light"
              >
                {{ $t('ledger_header_account') }}
              </b-badge>
              <b-badge
                v-if="!data.item.isTill && !data.item.is_active"
                variant="secondary"
              >
                {{ $t('ledger_inactive') }}
              </b-badge>
            </template>
          </template>
          <template #cell(actions)="data">
            <template v-if="!data.item.isTill && !data.item.isPartyHint && !data.item.partyKind">
              <b-link
                v-if="hasPermission('edit chart_of_accounts')"
                class="ml-1"
                @click="openEdit(data.item)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="16"
                />
              </b-link>
              <b-link
                v-if="canDelete(data.item)"
                class="ml-1 text-danger"
                @click="confirmDelete(data.item)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="16"
                />
              </b-link>
            </template>
          </template>
        </b-table>
      </div>

      <div class="d-md-none">
        <div
          v-for="row in mobileRoots"
          :key="row.rowKey"
        >
          <div
            v-if="row.isPartyHint"
            class="ledger-mobile-node text-muted small"
            :style="{ paddingInlineStart: `${row.depth * 12}px` }"
          >
            {{ row.isPartyEmpty ? $t('ledger_party_filter_empty') : $t('ledger_party_filter_more', { shown: row.shown, total: row.total }) }}
          </div>
          <div
            v-else
            class="ledger-mobile-node"
            :class="{ 'ledger-inactive': !row.is_active }"
            :style="{ paddingInlineStart: `${row.depth * 12}px` }"
            @click="toggleExpand(row)"
          >
            <div class="d-flex justify-content-between">
              <div>
                <strong>{{ row.code }}</strong>
                <b-badge
                  v-if="row.isPartyControl"
                  variant="light"
                  class="ml-50"
                >
                  {{ formatCount(row.childCount) }}
                </b-badge>
                <div>{{ accountName(row) }}</div>
                <b-badge
                  v-if="partyBadgeKey(row)"
                  variant="light-primary"
                  class="mt-25 mr-50"
                >
                  {{ $t(partyBadgeKey(row)) }}
                </b-badge>
                <b-badge
                  v-if="row.isTill"
                  variant="warning"
                  class="mt-25"
                >
                  {{ $t('ledger_till') }} · {{ tillTypeLabel(row.type) }}
                </b-badge>
                <b-badge
                  v-else
                  :variant="typeBadgeVariant(enumValue(row.type))"
                  class="mt-25"
                >
                  {{ enumLabel(row.type, $i18n.locale) }}
                </b-badge>
                <div
                  v-if="row.isTill"
                  class="ledger-num small"
                >
                  {{ money(row.current_balance) }}
                </div>
              </div>
              <feather-icon
                v-if="row.hasChildren"
                :icon="isExpanded(row) ? 'ChevronDownIcon' : (isRtl ? 'ChevronLeftIcon' : 'ChevronRightIcon')"
                size="18"
              />
            </div>
            <div
              v-if="!row.isTill && !row.partyKind && (hasPermission('edit chart_of_accounts') || canDelete(row))"
              class="mt-50"
              @click.stop
            >
              <b-button
                v-if="hasPermission('edit chart_of_accounts')"
                size="sm"
                variant="outline-primary"
                class="mr-50"
                @click="openEdit(row)"
              >
                {{ $t('ledger_edit_account') }}
              </b-button>
              <b-button
                v-if="canDelete(row)"
                size="sm"
                variant="outline-danger"
                @click="confirmDelete(row)"
              >
                {{ $t('ledger_delete') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <b-sidebar
      id="coa-drawer"
      v-model="drawerOpen"
      :title="editingId ? $t('ledger_edit_account') : $t('ledger_new_account')"
      right
      shadow
      backdrop
      width="420px"
    >
      <div class="px-2 pb-2">
        <b-form-group :label="$t('ledger_code')">
          <b-form-input v-model="form.code" />
        </b-form-group>
        <b-form-group :label="$t('ledger_name')">
          <b-form-input v-model="form.name" />
        </b-form-group>
        <b-form-group :label="$t('ledger_name_en')">
          <b-form-input v-model="form.name_en" />
        </b-form-group>
        <b-form-group :label="$t('ledger_type')">
          <v-select
            v-model="form.type"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="ACCOUNT_TYPES"
            :reduce="val => val.value"
            :label="localeLabel"
            @input="onTypeChange"
          />
        </b-form-group>
        <b-form-group :label="$t('ledger_nature')">
          <v-select
            v-model="form.nature"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="ACCOUNT_NATURES"
            :reduce="val => val.value"
            :label="localeLabel"
          />
        </b-form-group>
        <b-form-group :label="$t('ledger_parent')">
          <gl-account-select
            v-model="form.parent_id"
            :accounts="parentOptions"
            :exclude-ids="excludeParentIds"
            :placeholder="$t('ledger_parent')"
          />
        </b-form-group>
        <b-form-checkbox
          v-model="form.accepts_posting"
          class="mb-1"
        >
          {{ $t('ledger_accepts_posting') }}
        </b-form-checkbox>
        <b-form-checkbox
          v-model="form.is_active"
          class="mb-1"
        >
          {{ $t('ledger_active') }}
        </b-form-checkbox>
        <b-form-group :label="$t('ledger_analytic')">
          <b-form-input v-model="form.analytic_dimension" />
        </b-form-group>
        <b-form-group :label="$t('ledger_notes')">
          <b-form-textarea
            v-model="form.notes"
            rows="3"
          />
        </b-form-group>
        <div class="d-flex">
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="saving"
            @click="saveAccount"
          >
            {{ saving ? $t('ledger_saving') : $t('ledger_save') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="drawerOpen = false"
          >
            {{ $t('ledger_cancel') }}
          </b-button>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BBadge, BLink, BSidebar,
  BFormGroup, BFormCheckbox, BFormTextarea, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import {
  fetchAllChartAccounts, getChartOfAccountsTree, createChartOfAccount, updateChartOfAccount, deleteChartOfAccount,
} from '@/api/accounting/chartOfAccounts'
import {
  nestChartAccounts,
  enumValue, enumLabel, accountDisplayName, formatLedgerMoney,
  ACCOUNT_TYPES, ACCOUNT_NATURES, defaultNatureForType, typeBadgeVariant,
  descendantIds, findNode, apiErrorMessage, listFrom,
  isPartyControlCode, partyAccountKind,
} from '@/utils/ledger'
import GlAccountSelect from './components/GlAccountSelect.vue'

function filterOperationalTree(nodes, filter = {}) {
  const keyword = String(filter.keyword || '').trim().toLowerCase()
  const type = filter.type || null
  const posting = filter.accepts_posting
  const textMatch = node => {
    if (!keyword) return true
    return `${node.code || ''} ${node.name || ''} ${node.name_en || ''}`.toLowerCase().includes(keyword)
  }
  const glMatch = node => {
    if (node.node_kind === 'wallet' || node.node_kind === 'till') return false
    if (type && enumValue(node.type) !== type) return false
    if (posting === true && !node.accepts_posting) return false
    if (posting === false && node.accepts_posting) return false
    return textMatch(node)
  }
  const walk = list => (list || []).reduce((acc, node) => {
    if (node.node_kind === 'wallet') return acc
    const children = walk(node.children || [])
    const tills = (node.accounts || []).filter(till => till && till.node_kind !== 'wallet')
    const self = glMatch(node)
    const matchingTills = tills.filter(till => self || textMatch(till))
    if (!self && !children.length && !tills.some(till => textMatch(till))) return acc
    acc.push({
      ...node,
      node_kind: node.node_kind || 'gl',
      children,
      accounts: self ? tills : matchingTills,
    })
    return acc
  }, [])
  return walk(nodes)
}

const PARTY_OPEN_LIMIT = 80

function partyChildMatches(node, query) {
  if (!query) return true
  return `${node.code || ''} ${node.name || ''} ${node.name_en || ''}`.toLowerCase().includes(query)
}

function flattenOperationalTree(nodes, depth = 0, options = {}) {
  const expandedIds = options.expandedIds || null
  const forceOpen = Boolean(options.forceOpen)
  const partyQuery = String(options.partyQuery || '').trim().toLowerCase()
  const out = [];
  (nodes || []).forEach(node => {
    if (node.node_kind === 'wallet') return
    const tills = (node.accounts || []).filter(till => till && till.node_kind !== 'wallet')
    const children = node.children || []
    const isPartyControl = isPartyControlCode(node.code)
    const childCount = children.length
    const hasChildren = tills.length > 0 || children.length > 0
    const openedByUser = Boolean(expandedIds && expandedIds.has(node.id))
    const isOpen = isPartyControl ? openedByUser : (forceOpen || openedByUser)
    out.push({
      ...node,
      depth,
      hasChildren,
      childCount,
      isPartyControl,
      partyKind: partyAccountKind(node),
      node_kind: node.node_kind || 'gl',
      isTill: false,
      isPartyHint: false,
      rowKey: `gl-${node.id}`,
    })
    if (!hasChildren || !isOpen) return
    tills.forEach(till => {
      out.push({
        ...till,
        depth: depth + 1,
        hasChildren: false,
        childCount: 0,
        isPartyControl: false,
        partyKind: null,
        node_kind: 'till',
        isTill: true,
        isPartyHint: false,
        is_active: true,
        rowKey: `till-${node.id}-${till.id}`,
        parent_gl_id: node.id,
      })
    })
    let visibleChildren = children
    let partyHint = null
    if (isPartyControl) {
      const matched = children.filter(child => partyChildMatches(child, partyQuery))
      visibleChildren = matched.slice(0, PARTY_OPEN_LIMIT)
      const hiddenCount = matched.length - visibleChildren.length
      if (!matched.length) {
        partyHint = { empty: true, rowKey: `party-empty-${node.id}` }
      } else if (hiddenCount > 0) {
        partyHint = {
          empty: false,
          shown: visibleChildren.length,
          total: matched.length,
          rowKey: `party-more-${node.id}`,
        }
      }
    }
    out.push(...flattenOperationalTree(visibleChildren, depth + 1, options))
    if (partyHint) {
      out.push({
        depth: depth + 1,
        hasChildren: false,
        isTill: false,
        isPartyHint: true,
        isPartyEmpty: partyHint.empty,
        is_active: true,
        shown: partyHint.shown,
        total: partyHint.total,
        code: '',
        name: '',
        rowKey: partyHint.rowKey,
      })
    }
  })
  return out
}

function collectGlNodes(nodes, acc = []) {
  ;(nodes || []).forEach(node => {
    if (node.node_kind === 'wallet' || node.node_kind === 'till') return
    acc.push(node)
    collectGlNodes(node.children || [], acc)
  })
  return acc
}

const emptyForm = () => ({
  code: '',
  name: '',
  name_en: '',
  type: 'asset',
  nature: 'debit',
  parent_id: null,
  accepts_posting: true,
  is_active: true,
  analytic_dimension: '',
  notes: '',
})

export default {
  name: 'ChartOfAccountsList',
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BLink,
    BSidebar,
    BFormGroup,
    BFormCheckbox,
    BFormTextarea,
    BSpinner,
    vSelect,
    FeatherIcon,
    GlAccountSelect,
  },
  data() {
    return {
      ACCOUNT_TYPES,
      ACCOUNT_NATURES,
      loading: false,
      saving: false,
      accounts: [],
      tree: [],
      expandedIds: new Set(),
      searchTimeout: null,
      drawerOpen: false,
      editingId: null,
      form: emptyForm(),
      filter: {
        keyword: '',
        type: null,
        is_active: null,
        accepts_posting: null,
      },
      partyQuery: '',
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    localeLabel() {
      return this.$i18n.locale === 'en' ? 'label' : 'label_ar'
    },
    typeOptions() {
      return ACCOUNT_TYPES
    },
    activeOptions() {
      return [
        { value: true, label: 'Active', label_ar: this.$t('ledger_active') },
        { value: false, label: 'Inactive', label_ar: this.$t('ledger_inactive') },
      ]
    },
    postingOptions() {
      return [
        { value: true, label: 'Postable', label_ar: this.$t('ledger_posting_only') },
        { value: false, label: 'Header', label_ar: this.$t('ledger_headers_only') },
      ]
    },
    fields() {
      return [
        { key: 'code', label: this.$t('ledger_code') },
        { key: 'name', label: this.$t('ledger_name') },
        { key: 'type', label: this.$t('ledger_type') },
        { key: 'nature', label: this.$t('ledger_nature') },
        { key: 'flags', label: '' },
        { key: 'actions', label: this.$t('ledger_actions') },
      ]
    },
    visibleTree() {
      return filterOperationalTree(this.tree, {
        keyword: this.filter.keyword,
        type: this.filter.type,
        accepts_posting: this.filter.accepts_posting,
      })
    },
    treeIsFiltered() {
      return Boolean(
        String(this.filter.keyword || '').trim()
        || this.filter.type
        || this.filter.accepts_posting != null,
      )
    },
    flatRows() {
      return flattenOperationalTree(this.visibleTree, 0, {
        expandedIds: this.expandedIds,
        forceOpen: this.treeIsFiltered,
        partyQuery: this.partyQuery,
      })
    },
    mobileRoots() {
      return this.flatRows
    },
    partyParentOpen() {
      const ids = this.expandedIds
      const walk = nodes => (nodes || []).some(node => (
        (isPartyControlCode(node.code) && ids.has(node.id)) || walk(node.children)
      ))
      return walk(this.visibleTree)
    },
    parentOptions() {
      return this.accounts.filter(account => !partyAccountKind(account))
    },
    excludeParentIds() {
      if (!this.editingId) return []
      const node = findNode(this.tree, this.editingId)
      return [this.editingId, ...(node ? descendantIds(node) : [])]
    },
  },
  watch: {
    'filter.is_active': 'loadAccounts',
  },
  mounted() {
    this.loadAccounts()
  },
  methods: {
    enumValue,
    enumLabel,
    typeBadgeVariant,
    accountName(account) {
      return accountDisplayName(account, this.$i18n.locale)
    },
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    tillTypeLabel(type) {
      const key = `ledger_till_${type}`
      return this.$te(key) ? this.$t(key) : type
    },
    partyBadgeKey(row) {
      const kind = row && (row.partyKind || partyAccountKind(row))
      if (kind === 'customer') return 'ledger_party_customer'
      if (kind === 'supplier') return 'ledger_party_supplier'
      if (kind === 'subcontractor') return 'ledger_party_subcontractor'
      return ''
    },
    formatCount(value) {
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      return new Intl.NumberFormat(loc).format(Number(value) || 0)
    },
    canDelete(item) {
      if (!item || item.isTill || item.isPartyHint || item.partyKind) return false
      return this.hasPermission('delete chart_of_accounts') && !item.hasChildren
    },
    async loadAccounts() {
      this.loading = true
      try {
        if (this.filter.is_active === false) {
          this.accounts = await fetchAllChartAccounts({ is_active: false, per_page: 100 })
          this.tree = nestChartAccounts(this.accounts)
          return
        }
        const res = await getChartOfAccountsTree()
        this.tree = listFrom(res)
        this.accounts = collectGlNodes(this.tree)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    isExpanded(row) {
      return this.expandedIds.has(row.id)
    },
    expandableIds(nodes, acc = []) {
      ;(nodes || []).forEach(node => {
        const tills = (node.accounts || []).filter(till => till && till.node_kind !== 'wallet')
        const children = node.children || []
        if (tills.length || children.length) acc.push(node.id)
        this.expandableIds(children, acc)
      })
      return acc
    },
    expandAll() {
      this.expandedIds = new Set(this.expandableIds(this.visibleTree))
    },
    collapseAll() {
      this.expandedIds = new Set()
    },
    toggleExpand(row) {
      if (!row.hasChildren) return
      const next = new Set(this.expandedIds)
      if (next.has(row.id)) next.delete(row.id)
      else next.add(row.id)
      this.expandedIds = next
    },
    openCreate() {
      this.editingId = null
      this.form = emptyForm()
      this.drawerOpen = true
    },
    openEdit(item) {
      if (!item || item.isTill || item.isPartyHint || item.partyKind) return
      this.editingId = item.id
      this.form = {
        code: item.code || '',
        name: item.name || '',
        name_en: item.name_en || '',
        type: enumValue(item.type),
        nature: enumValue(item.nature),
        parent_id: item.parent_id,
        accepts_posting: !!item.accepts_posting,
        is_active: !!item.is_active,
        analytic_dimension: item.analytic_dimension || '',
        notes: item.notes || '',
      }
      this.drawerOpen = true
    },
    onTypeChange(type) {
      this.form.nature = defaultNatureForType(type)
    },
    payload() {
      return {
        code: this.form.code,
        name: this.form.name,
        name_en: this.form.name_en || null,
        type: this.form.type,
        nature: this.form.nature,
        parent_id: this.form.parent_id || null,
        accepts_posting: this.form.accepts_posting,
        is_active: this.form.is_active,
        analytic_dimension: this.form.analytic_dimension || null,
        notes: this.form.notes || null,
      }
    },
    async saveAccount() {
      this.saving = true
      try {
        if (this.editingId) {
          await updateChartOfAccount(this.editingId, this.payload())
        } else {
          await createChartOfAccount(this.payload())
        }
        this.toast(this.$t('ledger_created_success'), 'success')
        this.drawerOpen = false
        await this.loadAccounts()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.saving = false
      }
    },
    async confirmDelete(item) {
      const result = await Swal.fire({
        text: this.$t('ledger_confirm_delete_account'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_delete'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await deleteChartOfAccount(item.id)
        this.toast(this.$t('ledger_deleted_success'), 'success')
        await this.loadAccounts()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.ledger-dense-table td,
.ledger-dense-table th {
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
}
.ledger-inactive {
  opacity: 0.55;
}
.ledger-tree-toggle,
.ledger-tree-spacer {
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0.25rem;
  vertical-align: middle;
}
.ledger-tree-toggle {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.ledger-mobile-node {
  border-bottom: 1px solid #eee;
  padding: 0.75rem 0.25rem;
}
.ledger-sticky-head th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
</style>
